import classNames from 'classnames';
import {Formik, FormikProps} from 'formik';
import {PureComponent} from 'react';
import {withTranslation, WithTranslation} from 'react-i18next';

import {passwordApi} from 'api';
import {Button, FormGroup, Loader, UnauthorizedLayoutContent, notifier, PasswordInputFormik} from 'components';
import {ROUTE_PATH} from 'constants/routes';
import {UnauthorizedLayout} from 'layouts';
import {TRANSLATION_KEYS} from 'locale/translation-keys';
import {apiUtils} from 'utils';

const FORM_CONTROL_T_KEYS = TRANSLATION_KEYS.COMMON.FORM_CONTROL;
const PAGE_KEYS = TRANSLATION_KEYS.RESET_PASSWORD_PAGE;
const PASSWORD_ERRORS = TRANSLATION_KEYS.COMMON.PASSWORD_ERRORS;

type FormFields = {
    password: string;
    confirmPassword: string;
};

type FormTemplateProps = {
    validationErrors: string[] | null
} & WithTranslation & FormikProps<FormFields>;

const initialValues: FormFields = {
    password: '',
    confirmPassword: ''
};

const FormTemplate: React.FC<FormTemplateProps> = ({values, validationErrors, handleSubmit, t}: FormTemplateProps) => (
    <form
        className='tw-mt-2'
        onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
        }}
    >
        <FormGroup>
            <PasswordInputFormik
                required
                autoFocus
                name='password'
                autoComplete='new-password'
                label={t(FORM_CONTROL_T_KEYS.PASSWORD)}
            />
        </FormGroup>

        <FormGroup>
            <PasswordInputFormik
                required
                name='confirmPassword'
                autoComplete='new-password'
                label={t(FORM_CONTROL_T_KEYS.CONFIRM_PASSWORD)}
            />
        </FormGroup>

        {validationErrors?.map((error) => {
            const errorLabel = PASSWORD_ERRORS[error.toUpperCase()] || PASSWORD_ERRORS.DEFAULT;
            return (<div key={error} className='tw-text-alert'>{t(errorLabel)}</div>);
        })}

        <div className='tw-mt-12'>
            <Button
                type='submit'
                width='full'
                height='full'
                color='primary'
                disabled={!values.password || !values.confirmPassword}
            >
                {t(PAGE_KEYS.FORM.BUTTON.SAVE_PASSWORD)}
            </Button>
        </div>
    </form>
);

const WrappedFormTemplate = withTranslation()(FormTemplate);

type State = {
    token: string,
    isTokenValid: boolean,
    isLoading: boolean,
    email: string,
    passwordChanged: boolean
    validationErrors?: {password: string[]}
}

const initialState: State = {
    token: null,
    isTokenValid: false,
    isLoading: true,
    email: '',
    passwordChanged: false,
    validationErrors: null
};

type Props = {
    history: any;
} & WithTranslation;

class ResetPasswordPage extends PureComponent<Props, State> {

    readonly state = initialState;

    componentDidMount() {
        const urlParams = new URLSearchParams(window.location.search);
        const token = urlParams.get('token');

        this.setState({isLoading: true});
        passwordApi.validateToken(token).then((response) => {
            const email = response.data.email;
            const isTokenValid = response.data.isValid;
            this.setState({isLoading: false, email, isTokenValid});
        }, () => {
            this.setState({isLoading: false, isTokenValid: false});
        });

        this.setState({token});
    }

    handleSubmit = (values: FormFields) => {
        const {token} = this.state;
        const {password, confirmPassword} = values;

        if (password !== confirmPassword) {
            notifier.error({details: this.props.t(PASSWORD_ERRORS.PASSWORD_THE_SAME)});
            return;
        }

        this.setState({isLoading: true});
        passwordApi.resetPassword(token, password, confirmPassword).then(() => {
            this.setState({passwordChanged: true, isLoading: false});
        }, (error) => {
            this.setState({passwordChanged: false, isLoading: false});
            const errors = apiUtils.getResponseErrors(error);
            const validationErrors = apiUtils.getValidationErrors(error) || {};

            this.setState({validationErrors});
            if (errors) {
                notifier.error({details: errors});
            }
        });
    };

    render() {
        const {handleSubmit} = this;
        const {t} = this.props;
        const {email, isLoading, isTokenValid, passwordChanged, validationErrors} = this.state;

        return (
            <UnauthorizedLayout icon={passwordChanged ? 'completed' : 'lock-opened'}>
                <UnauthorizedLayoutContent
                    title={t(!isLoading && passwordChanged ? PAGE_KEYS.PASSWORD_CHANGED : PAGE_KEYS.TITLE)}
                    link={{
                        path: ROUTE_PATH.PUBLIC.LOGIN,
                        name: t(PAGE_KEYS.HAVE_PASSWORD_LINK_TEXT),
                        description: t(PAGE_KEYS.HAVE_PASSWORD_TEXT),
                        descriptionPage: t(PAGE_KEYS.HAVE_PASSWORD_TEXT_PAGE)
                    }}
                >
                    {isLoading && <div className='tw-relative tw-w-10 tw-h-10 tw-mx-auto'><Loader /></div>}

                    {!isLoading && !isTokenValid && !passwordChanged &&
                        <div className='tw-pt-4'>
                            {t(PAGE_KEYS.INVALID_TOKEN)}
                        </div>}

                    <div className={classNames({
                        'tw-hidden': isLoading || !isTokenValid || passwordChanged
                    })}
                    >
                        <div className='tw-mt-1 sm:tw-mt-4'>
                            {t(PAGE_KEYS.NEW_PASSWORD_FOR)}
                            <span className='tw-font-medium'>{email}</span>
                        </div>

                        <Formik
                            initialValues={initialValues}
                            onSubmit={handleSubmit}
                        >
                            {(props) => (
                                <WrappedFormTemplate
                                    {...props}
                                    validationErrors={validationErrors?.password}
                                />
                            )}
                        </Formik>
                    </div>
                </UnauthorizedLayoutContent>
            </UnauthorizedLayout>
        );
    }
}

export default withTranslation()(ResetPasswordPage);
